import { toast } from '@/components/toast';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { polotnoTypes } from '@/pages/AdvancedEditor/types';
import { countElementsByTypeAndPage, isElementOfType } from '@/pages/AdvancedEditor/utils';
import { reaction } from 'mobx';
import { useCallback, useEffect } from 'react';

const LIMIT = {
    [polotnoTypes.Image]: 10,
    [polotnoTypes.Video]: 5,
};

const LIMIT_EXCEEDED_MESSAGE = {
    [polotnoTypes.Image]: {
        title: 'Image limit reached',
        message: `You can only add up to ${LIMIT[polotnoTypes.Image]} images per scene.`,
    },
    [polotnoTypes.Video]: {
        title: 'Video/GIF limit reached',
        message: `You can only add up to ${LIMIT[polotnoTypes.Video]} videos or GIFs per scene.`,
    },
};

export const useMediaLimit = () => {
    const { store } = useAdvancedEditorContext();

    const handleLimitExceeded = useCallback(
        (type: polotnoTypes, limit: number) => {
            toast.info({
                title: LIMIT_EXCEEDED_MESSAGE[type as keyof typeof LIMIT_EXCEEDED_MESSAGE].title,
                message: LIMIT_EXCEEDED_MESSAGE[type as keyof typeof LIMIT_EXCEEDED_MESSAGE].message,
            });

            const lastInsertedElements = store.activePage?.children
                .filter(element =>
                    type === polotnoTypes.Image
                        ? isElementOfType(element, type) || isElementOfType(element, polotnoTypes.Svg)
                        : isElementOfType(element, type)
                )
                .slice(limit)
                .map(element => element.id);

            store.deleteElements(lastInsertedElements);
        },
        [store]
    );

    useEffect(() => {
        const dispose = reaction(
            () => store.activePage?.children?.length,
            () => {
                if (!store.activePage) {
                    return;
                }

                (Object.entries(LIMIT) as [polotnoTypes, number][]).forEach(([type, limit]) => {
                    const count =
                        type === polotnoTypes.Image
                            ? countElementsByTypeAndPage(store?.activePage, type) +
                              countElementsByTypeAndPage(store?.activePage, polotnoTypes.Svg)
                            : countElementsByTypeAndPage(store?.activePage, type);

                    if (count > limit) {
                        handleLimitExceeded(type, limit);
                    }
                });
            }
        );

        return () => {
            dispose();
        };
    }, [store, handleLimitExceeded]);
};
